// M23 — the subtitles overlay (accessibility): a stack of recent sound-event
// captions in the corner, each with a direction arrow, fading after a few seconds.
// The caption text comes from the pure `subtitleFor` (AUD-2); this file owns only
// the DOM + expiry. Off by default; the options `subtitles` toggle drives it.
import { h } from './h';
import { subtitleFor } from '../audio/synth/events';
import type { AudioEvent } from '../audio/engine';

export type SubtitleDir = 'left' | 'center' | 'right';
const ARROW: Record<SubtitleDir, string> = { left: '‹', center: '•', right: '›' };

interface Line { caption: string; dir: SubtitleDir; until: number; el: HTMLElement; }

/** Time a caption stays on screen. */
const TTL_MS = 3000;
const MAX_LINES = 4;

export class Subtitles {
  private readonly root: HTMLElement;
  private lines: Line[] = [];
  private enabled = false;

  constructor(parent: HTMLElement = document.body) {
    this.root = h('div', { class: 'bb-subtitles', style: { display: 'none' } });
    parent.append(this.root);
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    this.root.style.display = on ? 'flex' : 'none';
    if (!on) this.clear();
  }
  get isEnabled(): boolean { return this.enabled; }

  /** Caption an audio event (direction relative to the listener's forward look). */
  notify(e: AudioEvent, dir: SubtitleDir, now = performance.now()): void {
    if (!this.enabled) return;
    this.push(subtitleFor(e.id), dir, now);
  }

  /** Add a caption line, coalescing an immediate repeat and capping the stack. */
  push(caption: string, dir: SubtitleDir, now = performance.now()): void {
    const last = this.lines[this.lines.length - 1];
    if (last && last.caption === caption) { last.until = now + TTL_MS; last.dir = dir; this.render(last); return; }
    const el = h('div', { class: 'bb-subtitle' });
    const line: Line = { caption, dir, until: now + TTL_MS, el };
    this.render(line);
    this.lines.push(line);
    this.root.append(el);
    while (this.lines.length > MAX_LINES) this.expire(this.lines[0]!);
  }

  /** Drop expired lines (call each frame). */
  tick(now = performance.now()): void {
    for (const l of this.lines.slice()) if (l.until <= now) this.expire(l);
  }

  /** Active caption texts, newest last (test/E2E seam). */
  active(now = performance.now()): string[] {
    return this.lines.filter((l) => l.until > now).map((l) => l.caption);
  }

  private render(l: Line): void { l.el.textContent = `${ARROW[l.dir]} ${l.caption}`; }
  private expire(l: Line): void {
    l.el.remove();
    this.lines = this.lines.filter((x) => x !== l);
  }
  private clear(): void { for (const l of this.lines.slice()) this.expire(l); }
}

/** Left/center/right of a world-positioned event relative to the camera's look
 *  (XZ plane). Non-positional events (music/UI at origin) read as centre. */
export function subtitleDirection(e: AudioEvent, camX: number, camZ: number, fwdX: number, fwdZ: number): SubtitleDir {
  if (e.x === 0 && e.z === 0) return 'center';
  const tx = e.x - camX, tz = e.z - camZ;
  if (Math.hypot(tx, tz) < 0.5) return 'center';
  const cross = fwdX * tz - fwdZ * tx; // >0 → to the right of the look direction
  return cross > 0.3 ? 'right' : cross < -0.3 ? 'left' : 'center';
}
